"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.updateFinDiscountRuleSchema = exports.createFinDiscountRuleSchema = exports.finDiscountRuleShape = exports.finDiscountCalculationMethodEnum = void 0;
const zod_1 = require("zod");
const discount_rule_scope_1 = require("../domain/discount-rule-scope");
/** Enterprise Architecture Handbook v1.0 (OP Billing Administration, Module 3 — Discount Rules). Distinct from `pricing`'s item/drug `DiscountRule` — this is the OP-billing charge-level rule with `approvalMatrixId` reusing Platform's `ApprovalMatrix` unchanged. */
exports.finDiscountCalculationMethodEnum = zod_1.z.enum(['PERCENTAGE', 'FLAT_AMOUNT']);
exports.finDiscountRuleShape = {
    ruleCode: zod_1.z.string().min(1).max(30),
    name: zod_1.z.string().min(1).max(255),
    discountTypeId: zod_1.z.string().uuid().optional(),
    calculationMethod: exports.finDiscountCalculationMethodEnum,
    value: zod_1.z.number().int().positive(),
    maxDiscountAmount: zod_1.z.number().int().positive().optional(),
    priority: zod_1.z.number().int().min(0).optional(),
    requiresApproval: zod_1.z.boolean().optional(),
    approvalMatrixId: zod_1.z.string().uuid().optional(),
    couponCode: zod_1.z.string().min(1).max(50).optional(),
    /**
     * SCOPE — the account head this rule may discount (CONS FEE - OP for the
     * staff consultation concession). Omitted means the rule is not head-scoped,
     * and the staff-discount mechanism ignores it entirely: a rule that applied
     * to everything would fire on every line of every bill.
     */
    accountHeadId: zod_1.z.string().uuid().optional(),
    /**
     * A STAFF discount: it may only be given by naming the employee it is for.
     * The counter then supplies an EMPLOYEE and never an amount — this rule's
     * `value` is the figure, which is what makes it non-editable at the desk.
     */
    requiresEmployee: zod_1.z.boolean().optional(),
    /**
     * SCOPE, PER LINE — which service family this rule discounts.
     *
     * Desk, 2026-08-30: "first fix which is lab service, radiology service,
     * others. then each line can considerable." The staff concession is decided
     * line by line: a bill mixing a consultation with two lab tests takes the
     * flat amount on the consultation and the percentage on each test.
     *
     * Omitted means the rule is not family-scoped. APPOINTMENT (2026-10-17) is the doctor's consultation booked on
     * the Appointment screen; until a rule names it, the Appointment screen follows OTHER.
     */
    serviceFamily: zod_1.z.enum(['LAB', 'RADIOLOGY', 'LAB_RADIOLOGY', 'OTHER', 'APPOINTMENT', 'ALL']).optional(),
    /**
     * SCOPE, PER SERVICE (2026-10-17) - the services this rule names by code, e.g. 30% for staff on XRT12 and BCB2. Codes are
     * the charge codes the Service Master shows. May sit beside `serviceFamily` LAB / RADIOLOGY / LAB_RADIOLOGY ("Service Code +
     * Lab"): the rule then covers the listed services AND that family.
     */
    serviceCodes: zod_1.z.array(zod_1.z.string().trim().toUpperCase().regex(discount_rule_scope_1.SERVICE_CODE_PATTERN)).max(discount_rule_scope_1.SERVICE_CODES_MAX).optional(),
    /** SCOPE, PER ACCOUNT HEAD (2026-10-17) - the heads this rule covers: every service filed under one, and any line billed to it. */
    accountHeadIds: zod_1.z.array(zod_1.z.string().uuid()).max(discount_rule_scope_1.ACCOUNT_HEADS_MAX).optional(),
    /**
     * WHO the concession is for — the counter's dropdown. Omitted means EVERY
     * category, which is how one row serves staff, students, dependents,
     * retired staff and camps where the figure is the same.
     */
    beneficiaryCategory: zod_1.z.string().regex(discount_rule_scope_1.CONCESSION_TYPE_CODE_PATTERN, 'Not a concession type code').optional(),
    maxRedemptions: zod_1.z.number().int().positive().optional(),
    effectiveFrom: zod_1.z.coerce.date(),
    effectiveTo: zod_1.z.coerce.date().optional(),
};
exports.createFinDiscountRuleSchema = zod_1.z.object(exports.finDiscountRuleShape);
/**
 * An update may CLEAR a scope field (send null) - "all categories", "no account head" - which the create shape cannot say.
 * Still partial: `{ value }` changes the amount and nothing else.
 */
exports.updateFinDiscountRuleSchema = zod_1.z
    .object({
    ...exports.finDiscountRuleShape,
    accountHeadId: exports.finDiscountRuleShape.accountHeadId.nullable(),
    serviceFamily: exports.finDiscountRuleShape.serviceFamily.nullable(),
    beneficiaryCategory: exports.finDiscountRuleShape.beneficiaryCategory.nullable(),
    approvalMatrixId: exports.finDiscountRuleShape.approvalMatrixId.nullable(),
    effectiveTo: exports.finDiscountRuleShape.effectiveTo.nullable(),
    maxDiscountAmount: exports.finDiscountRuleShape.maxDiscountAmount.nullable(),
})
    .partial();
//# sourceMappingURL=fin-discount-rule.schema.js.map